import {useCallback, useEffect, useRef, useState} from "react";
import {usePathname} from "next/navigation";
import {
    applyBlockElements,
    debounce,
    getUsercentricsServices,
    resetIframeToBlocked,
    setIframeSrc,
    waitForApi
} from "./consentUtils";

interface ConsentStatus {
    [serviceId: string]: boolean | undefined;
}

const useOverlayService = () => {
    const [services, setServices] = useState<{
        name: string;
        selector: string;
        serviceId: string
    }[]>([]);
    const overlayStateRef = useRef<Record<string, boolean>>({});
    const iframeStateRef = useRef<Record<string, boolean>>({});
    const [isReady, setIsReady] = useState(false);
    // Initial consent that arrives before the services are loaded is cached in
    // a ref (no state -> no cascading renders).
    const pendingInitialConsentRef = useRef<ConsentStatus | null>(null);
    const pathname = usePathname();

    const blockOverlayForService = useCallback(
        (service: { name: string; selector: string; serviceId: string }) => {
            applyBlockElements(service);
        },
        []
    );

    const unblockIframe = useCallback((iframe: HTMLIFrameElement, serviceName: string) => {
        setIframeSrc(iframe, "Unblock iframe");
        iframeStateRef.current[serviceName] = true;
    }, []);

    const handleOverlayService = useCallback(
        (serviceName: string, consentStatus: ConsentStatus) => {
            const service = services.find((s) => s.name === serviceName);
            if (!service) {
                return;
            }
            const rawService = getUsercentricsServices()[service.serviceId];
            const hasConsentInRawService = rawService?.consent?.given === true;

            // Check if consent exists using service name in the consent status
            const consentByName = consentStatus[service.name];

            const currentConsent = hasConsentInRawService || consentByName;

            const iframes = document.querySelectorAll(service.selector);

            if (iframes.length === 0 && !overlayStateRef.current[service.name]) {
                return;
            }

            if (!currentConsent) {
                // Reset the iframe so Usercentrics can show the overlay again.
                iframes.forEach(
                    (iframe) => resetIframeToBlocked(iframe as HTMLIFrameElement));
                iframeStateRef.current[service.name] = false;
                overlayStateRef.current[service.name] = true;
                blockOverlayForService(service);
            } else {
                iframes.forEach(
                    (iframe) => unblockIframe(iframe as HTMLIFrameElement, serviceName));
                overlayStateRef.current[service.name] = false;
            }
        },
        [services, blockOverlayForService, unblockIframe]
    );

    const handleConsentStatusChange = useCallback(
        (consentStatus: ConsentStatus) => {
            services.forEach(({name}) => {
                handleOverlayService(name, consentStatus);
            });
        },
        [services, handleOverlayService]
    );

    // Synchronizes all iframes with the current consent state. Wrapped in a
    // useCallback so the ref access happens outside of render.
    const syncOverlaysToConsent = useCallback(() => {
        if (typeof document === 'undefined') return;
        services.forEach((service) => {
            const iframes = document.querySelectorAll(service.selector);
            iframes.forEach((iframe) => {
                if (iframeStateRef.current[service.name]) {
                    const iframeElement = iframe as HTMLIFrameElement;

                    // Set the iframe src and make it visible
                    setIframeSrc(iframeElement, "Resize handler");
                } else {
                    // Check if consent is given in the raw service object
                    const rawService = getUsercentricsServices()[service.serviceId];
                    const hasConsentInRawService = rawService?.consent?.given === true;

                    if (hasConsentInRawService) {
                        overlayStateRef.current[service.name] = false;
                        iframeStateRef.current[service.name] = true;

                        const iframeElement = iframe as HTMLIFrameElement;

                        // Set the iframe src and make it visible
                        setIframeSrc(iframeElement, "Resize handler consent");
                    } else if (overlayStateRef.current[service.name] !== false) {
                        blockOverlayForService(service);
                    }
                }
            });
        });
    }, [services, blockOverlayForService]);

    const handleInitialConsent = useCallback(
        (initialConsent: ConsentStatus) => {
            if (!isReady) {
                pendingInitialConsentRef.current = initialConsent;
                return;
            }
            services.forEach(({name}) => {
                handleOverlayService(name, initialConsent);
            });

            setTimeout(() => {
                syncOverlaysToConsent();
            }, 100);
        },
        [isReady, services, handleOverlayService, syncOverlaysToConsent]
    );

    useEffect(() => {
        let cancelled = false;

        // Async initialization: waits for the Usercentrics API and then stores
        // the detected services in state.
        waitForApi()
            .then(() => {
                if (cancelled) {
                    return;
                }
                const rawServices = getUsercentricsServices();
                const detectedServices = Object.entries(rawServices).map(
                    ([serviceId, service]) => ({
                        name: service.name,
                        selector: `iframe[data-service='${service.name}']`,
                        serviceId: serviceId,
                    }));
                setServices(detectedServices);
                setIsReady(true);
            })
            .catch((error) => {
                console.error(error);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (isReady && pendingInitialConsentRef.current) {
            const pending = pendingInitialConsentRef.current;
            pendingInitialConsentRef.current = null;
            handleInitialConsent(pending);
        }
    }, [isReady, handleInitialConsent]);

    useEffect(() => {
        // The debounce instance is created in the effect (not during render) so
        // that no ref access happens during render.
        const debouncedResizeHandler = debounce(syncOverlaysToConsent, 200);

        const handleConsentEvent = (e: CustomEvent<ConsentStatus & { action?: string }>) => {
            if (e.detail?.action === "onInitialPageLoad") {
                handleInitialConsent(e.detail);
            } else {
                handleConsentStatusChange(e.detail);
            }
        };

        window.addEventListener("consents_initialized", handleConsentEvent as EventListener);
        window.addEventListener("consent_changed", handleConsentEvent as EventListener);
        window.addEventListener("resize", debouncedResizeHandler);

        return () => {
            window.removeEventListener("consents_initialized", handleConsentEvent as EventListener);
            window.removeEventListener("consent_changed", handleConsentEvent as EventListener);
            window.removeEventListener("resize", debouncedResizeHandler);
        };
    }, [syncOverlaysToConsent, handleConsentStatusChange, handleInitialConsent]);

    useEffect(() => {
        // Debounced sync on route changes (mirrors the previous shared
        // debounce); the timer is cleared on unmount so nothing runs afterwards.
        const timer = setTimeout(() => {
            syncOverlaysToConsent();
        }, 200);

        return () => clearTimeout(timer);
    }, [pathname, syncOverlaysToConsent]);

    return {};
};

export default useOverlayService;
